import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { postJson } from '../../utils/apiClient';

const Signup = ({ onLogin }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [accountType, setAccountType] = useState('individual'); // 'individual' or 'company'
    const [formData, setFormData] = useState({
        // Account Type
        accountType: 'individual',
        
        // Step 1 - Account (Common)
        email: '',
        password: '',
        confirmPassword: '',

        // Step 2 - Individual Profile
        firstName: '',
        lastName: '',
        phone: '',
        location: '',

        // Step 2 - Company Profile
        companyName: '',
        companyWebsite: '',
        companySize: '',
        industry: '',
        companyPhone: '',
        companyLocation: '',
        companyDescription: '',

        // Step 3 - Professional (Individual only)
        headline: '',
        currentPosition: '',
        currentCompany: '',
        experienceYears: '',
        education: '',
        skills: '',

        // Step 3 - Company (Company only)
        establishedYear: '',
        companyType: '',
        benefits: '',
        socialLinks: '',
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAccountTypeChange = (type) => {
        setAccountType(type);
        setFormData(prev => ({
            ...prev,
            accountType: type
        }));
        // Reset to step 1 when changing account type
        setStep(1);
    };

    const validateStep = (step) => {
        setError('');

        if (step === 1) {
            if (!formData.email || !formData.password || !formData.confirmPassword) {
                setError('All fields are required');
                toast.error('All fields are required');
                return false;
            }
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                toast.error('Passwords do not match');
                return false;
            }
            if (formData.password.length < 8) {
                setError('Password must be at least 8 characters');
                toast.error('Password must be at least 8 characters');
                return false;
            }
        }

        if (step === 2) {
            if (accountType === 'individual') {
                if (!formData.firstName || !formData.lastName || !formData.location) {
                    setError('First name, last name, and location are required');
                    toast.error('First name, last name, and location are required');
                    return false;
                }
            } else {
                if (!formData.companyName || !formData.companyLocation || !formData.industry) {
                    setError('Company name, location, and industry are required');
                    toast.error('Company name, location, and industry are required');
                    return false;
                }
            }
        }

        if (step === 3) {
            if (accountType === 'individual') {
                if (!formData.experienceYears) {
                    setError('Please select your experience level');
                    toast.error('Please select your experience level');
                    return false;
                }
            }
        }

        return true;
    };

    const handleNext = () => {
        if (!validateStep(step)) return;
        setStep(prev => prev + 1);
    };

    const handleBack = () => {
        setStep(prev => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(step)) return;

        setLoading(true);
        setError('');

        try {
            // Prepare data based on account type
            const { confirmPassword, ...submitData } = formData;
            
            // Add account type to data
            submitData.accountType = accountType;

            const data = await postJson('signup.php', submitData);
            console.log(data);

            if (!data?.data?.user) {
                throw new Error('Signup response is missing user data.');
            }

            // Store token and user data when the backend returns a session.
            if (data.data.token) {
                localStorage.setItem('token', data.data.token);
            }
            localStorage.setItem('user', JSON.stringify(data.data.user));
            localStorage.setItem('userType', data.data.user.user_type || accountType);
            localStorage.setItem('accountType', accountType);

            if (onLogin && data.data.token) {
                onLogin(data.data);
            }

            toast.success('Account created successfully');

            if (data.data.token) {
                navigate(accountType === 'company' ? '/company/dashboard' : '/dashboard');
            } else {
                toast.success('Please sign in with your new account.');
                navigate('/login');
            }

        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
            toast.error(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const steps = accountType === 'individual' 
        ? [
            { number: 1, title: 'Account' },
            { number: 2, title: 'Profile' },
            { number: 3, title: 'Professional' },
          ]
        : [
            { number: 1, title: 'Account' },
            { number: 2, title: 'Company Info' },
            { number: 3, title: 'Details' },
          ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
            <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="md:flex">
                    {/* Left Column - Form */}
                    <div className="md:w-3/5 p-8 md:p-10">
                        <div className="mb-6">
                            <Link to="/" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-md flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">C</span>
                                </div>
                                <span className="text-2xl font-bold">Talencar</span>
                            </Link>
                            <h1 className="text-2xl font-bold text-gray-900 mt-4">Create your account</h1>
                            <p className="text-gray-600 mt-1">Join thousands of professionals</p>
                        </div>

                        {/* Account Type Selection */}
                        {step === 1 && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">I am registering as:</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleAccountTypeChange('individual')}
                                        className={`p-3 border-2 rounded-lg text-center transition-all duration-200 ${
                                            accountType === 'individual'
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        <span className="text-2xl mb-1 block">👤</span>
                                        <span className={`font-medium text-sm ${
                                            accountType === 'individual' ? 'text-blue-700' : 'text-gray-700'
                                        }`}>Individual</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAccountTypeChange('company')}
                                        className={`p-3 border-2 rounded-lg text-center transition-all duration-200 ${
                                            accountType === 'company'
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        <span className="text-2xl mb-1 block">🏢</span>
                                        <span className={`font-medium text-sm ${
                                            accountType === 'company' ? 'text-blue-700' : 'text-gray-700'
                                        }`}>Company</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Progress Steps */}
                        <div className="flex justify-between mb-6">
                            {steps.map((stepItem) => (
                                <div key={stepItem.number} className="flex flex-col items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= stepItem.number
                                        ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white'
                                        : 'bg-gray-200 text-gray-500'
                                        }`}>
                                        {step > stepItem.number ? '✓' : stepItem.number}
                                    </div>
                                    <span className={`text-xs mt-1 ${step >= stepItem.number ? 'text-blue-600 font-semibold' : 'text-gray-500'
                                        }`}>
                                        {stepItem.title}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Step 1 - Account (Common for both) */}
                            {step === 1 && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="you@example.com"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Password
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Create a strong password"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Confirm Password
                                        </label>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Confirm your password"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 2 - Individual Profile */}
                            {step === 2 && accountType === 'individual' && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                First Name
                                            </label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="John"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Last Name
                                            </label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Doe"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="+1 (555) 123-4567"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="San Francisco, CA"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 2 - Company Profile */}
                            {step === 2 && accountType === 'company' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="TechCorp Inc."
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Website
                                        </label>
                                        <input
                                            type="url"
                                            name="companyWebsite"
                                            value={formData.companyWebsite}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="https://www.techcorp.com"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Company Size
                                            </label>
                                            <select
                                                name="companySize"
                                                value={formData.companySize}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">Select Size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-500">201-500 employees</option>
                                                <option value="501-1000">501-1000 employees</option>
                                                <option value="1000+">1000+ employees</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Industry *
                                            </label>
                                            <select
                                                name="industry"
                                                value={formData.industry}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="">Select Industry</option>
                                                <option value="technology">Technology</option>
                                                <option value="finance">Finance</option>
                                                <option value="healthcare">Healthcare</option>
                                                <option value="education">Education</option>
                                                <option value="retail">Retail</option>
                                                <option value="manufacturing">Manufacturing</option>
                                                <option value="consulting">Consulting</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Location *
                                        </label>
                                        <input
                                            type="text"
                                            name="companyLocation"
                                            value={formData.companyLocation}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="San Francisco, CA"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Description
                                        </label>
                                        <textarea
                                            name="companyDescription"
                                            value={formData.companyDescription}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Tell us about your company..."
                                            rows="2"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3 - Individual Professional Info */}
                            {step === 3 && accountType === 'individual' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Professional Headline
                                        </label>
                                        <input
                                            type="text"
                                            name="headline"
                                            value={formData.headline}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g., Senior Software Engineer"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Current Position
                                            </label>
                                            <input
                                                type="text"
                                                name="currentPosition"
                                                value={formData.currentPosition}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Software Engineer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Company
                                            </label>
                                            <input
                                                type="text"
                                                name="currentCompany"
                                                value={formData.currentCompany}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="TechCorp"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Years of Experience
                                        </label>
                                        <select
                                            name="experienceYears"
                                            value={formData.experienceYears}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Select Experience</option>
                                            <option value="0-1">0-1 years</option>
                                            <option value="1-3">1-3 years</option>
                                            <option value="3-5">3-5 years</option>
                                            <option value="5-10">5-10 years</option>
                                            <option value="10+">10+ years</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Education
                                        </label>
                                        <textarea
                                            name="education"
                                            value={formData.education}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Degree, University, Year"
                                            rows="2"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Skills (comma separated)
                                        </label>
                                        <input
                                            type="text"
                                            name="skills"
                                            value={formData.skills}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="React, JavaScript, Python"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3 - Company Details */}
                            {step === 3 && accountType === 'company' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Established Year
                                        </label>
                                        <select
                                            name="establishedYear"
                                            value={formData.establishedYear}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Year</option>
                                            {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Type
                                        </label>
                                        <select
                                            name="companyType"
                                            value={formData.companyType}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Type</option>
                                            <option value="private">Private</option>
                                            <option value="public">Public</option>
                                            <option value="nonprofit">Non-profit</option>
                                            <option value="startup">Startup</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Employee Benefits
                                        </label>
                                        <textarea
                                            name="benefits"
                                            value={formData.benefits}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Health insurance, 401k, Remote work, etc."
                                            rows="2"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Social Media Links
                                        </label>
                                        <input
                                            type="text"
                                            name="socialLinks"
                                            value={formData.socialLinks}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="LinkedIn, Twitter, Facebook URLs"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Terms and Conditions - Final Step */}
                            {step === 3 && (
                                <div className="flex items-center space-x-2 mt-4">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        required
                                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="terms" className="text-xs text-gray-600">
                                        I agree to the <Link to="/terms" className="text-blue-600 hover:underline">Terms</Link> and <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                                    </label>
                                </div>
                            )}

                            <div className="flex justify-between pt-4">
                                {step > 1 && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="px-5 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 text-sm"
                                        disabled={loading}
                                    >
                                        Back
                                    </button>
                                )}

                                {step < 3 ? (
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        className="ml-auto px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-500 flex items-center space-x-1 transition-all duration-200 disabled:opacity-50 text-sm"
                                        disabled={loading}
                                    >
                                        <span>Continue</span>
                                        <span>→</span>
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="ml-auto px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-600 flex items-center space-x-1 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 text-sm"
                                        disabled={loading}
                                    >
                                        <span>{loading ? 'Creating...' : 'Create Account'}</span>
                                        <span>✓</span>
                                    </button>
                                )}
                            </div>
                        </form>

                        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-600">
                                Already have an account?{' '}
                                <Link to="/login" className="text-blue-600 font-semibold hover:text-blue-700">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Right Column - Simplified */}
                    <div className="hidden md:block md:w-2/5 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 p-8 relative">
                        <div className="relative z-10 text-white">
                            <h2 className="text-2xl font-bold mb-4">
                                {accountType === 'individual' 
                                    ? 'Find Your Dream Job' 
                                    : 'Hire the Best Talent'}
                            </h2>
                            <p className="text-blue-100 text-sm mb-6">
                                {accountType === 'individual'
                                    ? 'Join thousands of professionals who found their perfect role'
                                    : 'Connect with top candidates and streamline your hiring'}
                            </p>
                            
                            <div className="space-y-4">
                                {accountType === 'individual' ? (
                                    <>
                                        <div className="flex items-center space-x-3">
                                            <span className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-lg">🎯</span>
                                            <div>
                                                <h3 className="font-semibold text-sm">Personalized Matches</h3>
                                                <p className="text-blue-100 text-xs">Jobs based on your skills</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <span className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-lg">⚡</span>
                                            <div>
                                                <h3 className="font-semibold text-sm">Quick Apply</h3>
                                                <p className="text-blue-100 text-xs">Apply with one click</p>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center space-x-3">
                                            <span className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-lg">🔍</span>
                                            <div>
                                                <h3 className="font-semibold text-sm">Smart Search</h3>
                                                <p className="text-blue-100 text-xs">Find perfect candidates</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <span className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-lg">📋</span>
                                            <div>
                                                <h3 className="font-semibold text-sm">Easy Tracking</h3>
                                                <p className="text-blue-100 text-xs">Manage applications</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-3">
                                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                                    <span className="text-xl font-bold">10k+</span>
                                    <p className="text-blue-100 text-xs">
                                        {accountType === 'individual' ? 'Jobs' : 'Candidates'}
                                    </p>
                                </div>
                                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                                    <span className="text-xl font-bold">95%</span>
                                    <p className="text-blue-100 text-xs">Success</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Decorative Elements */}
                        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-400/20 rounded-full blur-3xl"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;
